import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bookmark, Trophy, Users, Plus, Trash2, BookOpen, Crown, GraduationCap, Globe2, Check, Heart, FileText, Mail, Send, Loader2, BellRing, Eye, EyeOff, Lightbulb, ArrowRight, Award, Share2, Download } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { certificateToBlob } from "../lib/certificateImage";
import { useAuth } from "../context/AuthContext";
import { Spinner, SectionLabel, Button, Pill } from "../components/Primitives";

const ICONS = { kingdom: Crown, lesson: BookOpen, quiz: GraduationCap };
const PATHS = { kingdom: "kingdoms", lesson: "lessons", quiz: "quizzes" };

export default function Dashboard() {
  const { user } = useAuth();
  const [portalLoading, setPortalLoading] = useState(false);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const r = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = r.data.portal_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open the billing portal");
      setPortalLoading(false);
    }
  };
  const [bookmarks, setBookmarks] = useState([]);
  const [progress, setProgress] = useState([]);
  const [learners, setLearners] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [hearted, setHearted] = useState([]);
  const [feedbackItems, setFeedbackItems] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [level, setLevel] = useState("");
  const [certShare, setCertShare] = useState(null);
  const [certName, setCertName] = useState("");

  const load = async () => {
    const [b, p, l, a, h, f, c] = await Promise.all([
      api.get("/bookmarks"),
      api.get("/progress"),
      api.get("/learners"),
      api.get("/assignments"),
      api.get("/stories/liked").catch(() => ({ data: [] })),
      api.get("/coursework/my-feedback").catch(() => ({ data: [] })),
      api.get("/certificates").catch(() => ({ data: [] })),
    ]);
    setBookmarks(b.data);
    setProgress(p.data);
    setLearners(l.data);
    setAssignments(a.data);
    setHearted(h.data);
    setFeedbackItems(f.data);
    setCertificates(c.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const addLearner = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    await api.post("/learners", { name: name.trim(), level: level.trim() });
    setName(""); setLevel("");
    toast.success("Learner added");
    load();
  };

  const removeLearner = async (id) => {
    await api.delete(`/learners/${id}`);
    load();
  };

  const toggleAssignment = async (a) => {
    const next = a.status === "completed" ? "assigned" : "completed";
    await api.patch(`/assignments/${a.id}`, { status: next });
    setAssignments((prev) => prev.map((x) => (x.id === a.id ? { ...x, status: next } : x)));
  };

  const removeAssignment = async (id) => {
    await api.delete(`/assignments/${id}`);
    setAssignments((prev) => prev.filter((x) => x.id !== id));
  };

  if (loading) return <div className="min-h-[60vh]"><Spinner /></div>;

  const quizzesDone = progress.filter((p) => p.item_type === "quiz").length;
  const unseenFeedback = feedbackItems.filter((f) => !f.feedback_seen);

  // Latest graded submission per coursework slug, for learner averages.
  const latestScoreBySlug = {};
  for (const f of feedbackItems) {
    if (f.score != null && !(f.slug in latestScoreBySlug)) latestScoreBySlug[f.slug] = f.score;
  }
  const learnerAvg = (l) => {
    const scores = assignments
      .filter((a) => a.learner_id === l.id && a.item_type === "coursework" && a.slug in latestScoreBySlug)
      .map((a) => latestScoreBySlug[a.slug]);
    if (!scores.length) return null;
    return Math.round(scores.reduce((s, x) => s + x, 0) / scores.length);
  };
  const certSlugs = new Set(certificates.map((c) => c.coursework_slug));
  const learnerCerts = (l) =>
    assignments.filter((a) => a.learner_id === l.id && a.item_type === "coursework" && certSlugs.has(a.slug)).length;

  const openCertShare = (e, cert) => {
    e.preventDefault();
    e.stopPropagation();
    setCertName(user?.name || "");
    setCertShare(cert);
  };

  const generateCert = async (cert, name, mode) => {
    try {
      const blob = await certificateToBlob(cert, name);
      const file = new File([blob], "certificate.png", { type: "image/png" });
      const text = `🏅 ${name} achieved ${cert.score}% on "${cert.title}" via Our African Narratives`;
      if (mode === "share" && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Certificate of Achievement", text });
        setCertShare(null);
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `certificate-${cert.coursework_slug}.png`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(mode === "share" ? "Certificate image downloaded — attach it to your post" : "Certificate image downloaded");
      setCertShare(null);
    } catch (err) {
      if (err?.name !== "AbortError") toast.error("Could not create the certificate image");
    }
  };

  return (
    <div data-testid="dashboard-page" className="mx-auto max-w-7xl px-6 py-12 md:px-12">
      <SectionLabel>Your Archive</SectionLabel>
      <h1 className="font-serif text-4xl font-semibold text-bone lg:text-5xl">Welcome, {user?.name?.split(" ")[0] || "Scholar"}.</h1>

      {user && !user.is_premium && (
        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border border-gold/60 bg-gold/5 p-5" data-testid="premium-upsell-card">
          <div>
            <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-gold">
              <Crown className="h-4 w-4" /> Premium
            </p>
            <p className="mt-1 text-sm text-muted">Unlock Lessons, Quizzes, Assignments, the AI Tutor and Certificates — $3/month.</p>
          </div>
          <Link to="/premium">
            <Button variant="gold" data-testid="dashboard-premium-btn">Go Premium</Button>
          </Link>
        </div>
      )}

      {user?.is_premium && user?.stripe_subscription_id && (
        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border border-edge bg-ash p-5" data-testid="manage-subscription-card">
          <div>
            <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-gold">
              <Crown className="h-4 w-4" /> Premium Active
            </p>
            <p className="mt-1 text-sm text-muted">Update your card, see invoices, or cancel anytime — handled securely by Stripe.</p>
          </div>
          <Button variant="outline" onClick={openPortal} disabled={portalLoading} data-testid="manage-subscription-btn">
            {portalLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Manage Subscription
          </Button>
        </div>
      )}

      {/* NEW FEEDBACK ALERT */}
      {unseenFeedback.length > 0 && (
        <div className="mt-8 border border-gold/60 bg-gold/10 p-5" data-testid="feedback-alert">
          <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-gold">
            <BellRing className="h-4 w-4" /> New feedback on your work
          </p>
          <div className="mt-3 space-y-2">
            {unseenFeedback.map((f) => (
              <Link key={f.submission_id} to={`/assignments/${f.slug}`} data-testid={`feedback-alert-${f.submission_id}`}
                className="group flex flex-wrap items-center gap-3 text-sm">
                <span className="text-bone group-hover:text-gold">{f.title}</span>
                {f.score != null && <span className="border border-gold/50 bg-gold/10 px-2 py-0.5 font-mono text-xs text-gold">{f.score}%</span>}
                {f.feedback && <span className="max-w-md truncate text-muted">“{f.feedback}”</span>}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* STATS */}
      <div className="mt-10 grid gap-px overflow-hidden border border-edge bg-edge sm:grid-cols-3">
        <Stat icon={Bookmark} label="Bookmarks" value={bookmarks.length} />
        <Stat icon={Trophy} label="Quizzes Completed" value={quizzesDone} />
        <Stat icon={Users} label="Tracked Learners" value={learners.length} />
      </div>

      {/* CERTIFICATE GALLERY */}
      {certificates.length > 0 && (
        <div className="mt-12" data-testid="certificate-gallery">
          <h2 className="mb-5 font-serif text-3xl text-bone">Certificates Earned</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {certificates.map((c) => (
              <Link key={c.submission_id} to={`/assignments/${c.coursework_slug}`} data-testid={`certificate-card-${c.submission_id}`}
                className="group border border-gold/40 bg-ash p-5 transition-colors hover:border-gold">
                <div className="flex items-start justify-between gap-2">
                  <Award className="h-7 w-7 text-gold" />
                  <span className="border border-gold/50 bg-gold/10 px-2 py-0.5 font-mono text-xs text-gold">{c.score}%</span>
                </div>
                <p className="mt-3 font-serif text-lg leading-snug text-bone group-hover:text-gold">{c.title}</p>
                <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.12em] text-muted">
                  {c.awarded_at ? new Date(c.awarded_at).toLocaleDateString() : ""} · {c.awarded_by}
                </p>
                <div className="mt-3 flex items-center justify-between">
                  <p className="flex items-center gap-1 text-xs text-muted group-hover:text-gold">
                    View & print <ArrowRight className="h-3 w-3" />
                  </p>
                  <div className="flex gap-1">
                    <button onClick={(e) => openCertShare(e, c)} data-testid={`share-cert-${c.submission_id}`}
                      title="Share to social media"
                      className="border border-edge p-1.5 text-muted transition-colors hover:border-gold hover:text-gold">
                      <Share2 className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={(e) => openCertShare(e, c)} data-testid={`download-cert-${c.submission_id}`}
                      title="Download certificate image"
                      className="border border-edge p-1.5 text-muted transition-colors hover:border-gold hover:text-gold">
                      <Download className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* CLASS PROGRESS BOARD */}
      {learners.length > 0 && (
        <div className="mt-12" data-testid="progress-board">
          <h2 className="mb-5 font-serif text-3xl text-bone">Class Progress</h2>
          <div className="divide-y divide-edge border border-edge bg-ash">
            {learners.map((l) => {
              const la = assignments.filter((a) => a.learner_id === l.id);
              const done = la.filter((a) => a.status === "completed").length;
              const pct = la.length ? Math.round((done / la.length) * 100) : 0;
              return (
                <div key={l.id} className="p-5" data-testid={`progress-row-${l.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-baseline gap-3">
                      <Link to={`/learners/${l.id}`} data-testid={`learner-profile-link-${l.id}`}
                        className="font-serif text-xl text-bone transition-colors hover:text-gold">{l.name}</Link>
                      {l.level && <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-muted">{l.level}</span>}
                    </div>
                    <span className="flex items-center gap-3">
                      {learnerCerts(l) > 0 && (
                        <span className="flex items-center gap-1 border border-gold/50 bg-gold/10 px-2 py-0.5 font-mono text-xs text-gold" data-testid={`cert-count-${l.id}`}>
                          <Award className="h-3 w-3" /> {learnerCerts(l)} {learnerCerts(l) === 1 ? "cert" : "certs"}
                        </span>
                      )}
                      {learnerAvg(l) != null && (
                        <span className="border border-gold/50 bg-gold/10 px-2 py-0.5 font-mono text-xs text-gold" data-testid={`avg-score-${l.id}`}>
                          Avg {learnerAvg(l)}%
                        </span>
                      )}
                      <span className="font-mono text-xs text-gold" data-testid={`progress-count-${l.id}`}>
                        {la.length === 0 ? "Nothing assigned yet" : `${done} / ${la.length} completed`}
                      </span>
                    </span>
                  </div>
                  {la.length > 0 && (
                    <>
                      <div className="mt-3 h-1.5 w-full bg-edge">
                        <div className="h-1.5 bg-gold transition-all" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {la.map((a) => (
                          <Link key={a.id} to={a.item_type === "coursework" ? `/assignments/${a.slug}` : "/globe"}
                            data-testid={`progress-chip-${a.id}`}
                            className={`flex items-center gap-1.5 border px-3 py-1.5 text-xs transition-colors ${a.status === "completed" ? "border-gold/60 bg-gold/10 text-gold" : "border-edge text-bone hover:border-gold/50"}`}>
                            {a.item_type === "coursework" ? <FileText className="h-3.5 w-3.5" /> : <Globe2 className="h-3.5 w-3.5" />}
                            {a.title}
                            {a.status === "completed" && <Check className="h-3.5 w-3.5" />}
                          </Link>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-12 grid gap-12 lg:grid-cols-12">
        {/* BOOKMARKS */}
        <div className="lg:col-span-7">
          <h2 className="mb-5 font-serif text-3xl text-bone">Bookmarks</h2>
          {bookmarks.length === 0 ? (
            <p className="border border-dashed border-edge p-8 text-center text-muted" data-testid="empty-bookmarks">
              No bookmarks yet. Save kingdoms and lessons as you explore.
            </p>
          ) : (
            <div className="space-y-3">
              {bookmarks.map((b) => {
                const Icon = ICONS[b.item_type] || BookOpen;
                return (
                  <Link
                    key={b.id}
                    to={`/${PATHS[b.item_type] || "lessons"}/${b.item_id}`}
                    data-testid={`bookmark-${b.item_id}`}
                    className="group flex items-center gap-4 border border-edge bg-ash p-4 transition-colors hover:border-gold/50"
                  >
                    <Icon className="h-5 w-5 text-gold" />
                    <span className="flex-1 text-bone group-hover:text-gold">{b.title}</span>
                    <Pill tone="muted">{b.item_type}</Pill>
                  </Link>
                );
              })}
            </div>
          )}

          <h2 className="mb-5 mt-12 font-serif text-3xl text-bone">Recent Progress</h2>
          {progress.length === 0 ? (
            <p className="border border-dashed border-edge p-8 text-center text-muted" data-testid="empty-progress">
              Complete a quiz to track your progress.
            </p>
          ) : (
            <div className="space-y-3">
              {progress.map((p, i) => (
                <div key={i} className="flex items-center justify-between border border-edge bg-ash p-4" data-testid={`progress-${p.item_id}`}>
                  <span className="text-bone">{p.title}</span>
                  {p.score != null && p.total != null ? (
                    <span className="font-mono text-sm text-gold">{p.score} / {p.total}</span>
                  ) : (
                    <Pill tone="forest">{p.status}</Pill>
                  )}
                </div>
              ))}
            </div>
          )}

          <h2 className="mb-5 mt-12 font-serif text-3xl text-bone">Stories You've Hearted</h2>
          {hearted.length === 0 ? (
            <p className="border border-dashed border-edge p-8 text-center text-muted" data-testid="empty-hearted">
              No hearts yet. Visit the <Link to="/stories" className="text-gold hover:underline">Heritage Stories</Link> and react to the ones that move you.
            </p>
          ) : (
            <div className="space-y-3" data-testid="hearted-stories">
              {hearted.map((s) => (
                <Link key={s.id} to={`/stories/${s.id}`} data-testid={`hearted-${s.id}`}
                  className="group flex items-center gap-4 border border-edge bg-ash p-4 transition-colors hover:border-gold/50">
                  <Heart className="h-5 w-5 flex-shrink-0 fill-current text-[#e07a72]" />
                  <span className="flex-1 text-bone group-hover:text-gold">{s.title}</span>
                  <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-muted">{s.year_label} · {s.region}</span>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* LEARNERS */}
        <div className="lg:col-span-5">
          <DigestCard />
          <TutorSuggestions />
          <h2 className="mb-5 font-serif text-3xl text-bone">Tracked Learners</h2>
          <form onSubmit={addLearner} className="border border-edge bg-ash p-5" data-testid="learner-form">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Learner name"
              data-testid="learner-name-input"
              className="mb-3 w-full border border-edge bg-ink px-4 py-3 text-bone placeholder:text-muted focus:border-gold focus:outline-none"
            />
            <input
              value={level}
              onChange={(e) => setLevel(e.target.value)}
              placeholder="Level / grade (optional)"
              data-testid="learner-level-input"
              className="mb-3 w-full border border-edge bg-ink px-4 py-3 text-bone placeholder:text-muted focus:border-gold focus:outline-none"
            />
            <Button type="submit" variant="gold" className="w-full" data-testid="add-learner-btn">
              <Plus className="h-4 w-4" /> Add Learner
            </Button>
          </form>

          <div className="mt-5 space-y-3">
            {learners.map((l) => {
              const la = assignments.filter((a) => a.learner_id === l.id);
              return (
                <div key={l.id} className="border border-edge bg-ash p-4" data-testid={`learner-${l.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-bone">{l.name}</p>
                      {l.level && <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-muted">{l.level}</p>}
                    </div>
                    <button onClick={() => removeLearner(l.id)} data-testid={`delete-learner-${l.id}`} className="text-muted hover:text-blood">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  {la.length > 0 && (
                    <div className="mt-4 space-y-2 border-t border-edge pt-3" data-testid={`assignments-${l.id}`}>
                      <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-gold">
                        <Globe2 className="h-3.5 w-3.5" /> Assigned Work
                      </p>
                      {la.map((a) => (
                        <div key={a.id} className="flex items-center gap-2" data-testid={`assignment-${a.id}`}>
                          <button
                            onClick={() => toggleAssignment(a)}
                            data-testid={`toggle-assignment-${a.id}`}
                            className={`flex h-4 w-4 flex-shrink-0 items-center justify-center border ${a.status === "completed" ? "border-gold bg-gold" : "border-edge"}`}
                            title={a.status === "completed" ? "Mark as assigned" : "Mark as completed"}
                          >
                            {a.status === "completed" && <Check className="h-3 w-3 text-ink" />}
                          </button>
                          {a.item_type === "coursework" && <FileText className="h-3.5 w-3.5 flex-shrink-0 text-gold" />}
                          <Link to={a.item_type === "coursework" ? `/assignments/${a.slug}` : "/globe"} className={`flex-1 text-sm hover:text-gold ${a.status === "completed" ? "text-muted line-through" : "text-bone"}`}>
                            {a.title}
                          </Link>
                          <button onClick={() => removeAssignment(a.id)} data-testid={`delete-assignment-${a.id}`} className="text-muted hover:text-blood">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {certShare && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          onClick={() => setCertShare(null)} data-testid="cert-share-modal">
          <div className="w-full max-w-md border border-edge bg-ink p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="mb-1 flex items-center gap-2 font-serif text-2xl text-bone"><Award className="h-5 w-5 text-gold" /> Share this achievement</h3>
            <p className="mb-4 text-xs text-muted">{certShare.title} · {certShare.score}%</p>
            <label className="mb-1 block font-mono text-[11px] uppercase tracking-[0.15em] text-muted">Name on the certificate</label>
            <input value={certName} onChange={(e) => setCertName(e.target.value)} data-testid="cert-share-name-input"
              placeholder="Who earned it?"
              className="w-full border border-edge bg-ash px-4 py-3 text-bone placeholder:text-muted focus:border-gold focus:outline-none" />
            <div className="mt-2 flex flex-wrap gap-2">
              <button onClick={() => setCertName(user?.name || "")} data-testid="cert-name-chip-me"
                className="border border-edge px-3 py-1 text-xs text-muted transition-colors hover:border-gold hover:text-gold">
                {user?.name?.split(" ")[0] || "Me"}
              </button>
              {learners.map((l) => (
                <button key={l.id} onClick={() => setCertName(l.name)} data-testid={`cert-name-chip-${l.id}`}
                  className="border border-edge px-3 py-1 text-xs text-muted transition-colors hover:border-gold hover:text-gold">
                  {l.name}
                </button>
              ))}
            </div>
            <div className="mt-5 flex gap-2">
              <button onClick={() => generateCert(certShare, certName.trim() || "Student", "share")} data-testid="cert-share-confirm"
                className="flex flex-1 items-center justify-center gap-2 border border-gold bg-gold px-4 py-2.5 text-sm font-semibold text-ink transition-colors hover:bg-transparent hover:text-gold">
                <Share2 className="h-4 w-4" /> Share
              </button>
              <button onClick={() => generateCert(certShare, certName.trim() || "Student", "download")} data-testid="cert-download-confirm"
                className="flex flex-1 items-center justify-center gap-2 border border-edge px-4 py-2.5 text-sm text-bone transition-colors hover:border-gold hover:text-gold">
                <Download className="h-4 w-4" /> Download image
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DigestCard() {
  const [settings, setSettings] = useState(null);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    api.get("/digest/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const toggle = async () => {
    const next = !settings.opt_in;
    setSettings((s) => ({ ...s, opt_in: next }));
    try {
      await api.post("/digest/settings", { opt_in: next });
      toast.success(next ? "Weekly digest on — arrives Mondays" : "Weekly digest off");
    } catch {
      setSettings((s) => ({ ...s, opt_in: !next }));
      toast.error("Could not update");
    }
  };

  const toggleStoryEmails = async () => {
    const next = !settings.story_emails;
    setSettings((s) => ({ ...s, story_emails: next }));
    try {
      await api.post("/digest/settings", { story_emails: next });
      toast.success(next ? "Story emails on — hearts & Story of the Month notices" : "Story emails paused");
    } catch {
      setSettings((s) => ({ ...s, story_emails: !next }));
      toast.error("Could not update");
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      await api.post("/digest/send-now", { origin: window.location.origin });
      toast.success(`Digest sent to ${settings.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send digest");
    } finally {
      setSending(false);
    }
  };

  const togglePreview = async () => {
    if (showPreview) return setShowPreview(false);
    if (!preview) {
      setLoadingPreview(true);
      try {
        const r = await api.get("/digest/preview");
        setPreview(r.data);
      } catch {
        return toast.error("Could not load the preview");
      } finally {
        setLoadingPreview(false);
      }
    }
    setShowPreview(true);
  };

  if (!settings) return null;

  return (
    <div className="mb-8 border border-edge bg-ash p-5" data-testid="digest-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-gold" />
          <div>
            <p className="text-bone">Weekly Digest Email</p>
            <p className="text-xs text-muted">Completions, grades & new stories — Mondays, to {settings.email}</p>
          </div>
        </div>
        <button onClick={toggle} data-testid="digest-toggle" role="switch" aria-checked={settings.opt_in}
          className={`relative h-6 w-11 flex-shrink-0 border transition-colors ${settings.opt_in ? "border-gold bg-gold" : "border-edge bg-ink"}`}>
          <span className={`absolute top-0.5 h-4.5 w-4.5 transition-all ${settings.opt_in ? "right-0.5 h-5 w-5 bg-ink" : "left-0.5 h-5 w-5 bg-edge"}`} />
        </button>
      </div>
      <div className="mt-4 flex items-center justify-between gap-3 border-t border-edge pt-4">
        <div className="flex items-center gap-2">
          <Heart className="h-5 w-5 text-[#e07a72]" />
          <div>
            <p className="text-bone">Story Emails</p>
            <p className="text-xs text-muted">Heart notices & Story of the Month congratulations for your stories</p>
          </div>
        </div>
        <button onClick={toggleStoryEmails} data-testid="story-emails-toggle" role="switch" aria-checked={settings.story_emails}
          className={`relative h-6 w-11 flex-shrink-0 border transition-colors ${settings.story_emails ? "border-gold bg-gold" : "border-edge bg-ink"}`}>
          <span className={`absolute top-0.5 transition-all ${settings.story_emails ? "right-0.5 h-5 w-5 bg-ink" : "left-0.5 h-5 w-5 bg-edge"}`} />
        </button>
      </div>
      {!settings.configured ? (
        <p className="mt-3 border border-dashed border-gold/40 bg-gold/5 p-3 text-xs text-muted" data-testid="digest-not-configured">
          Email sending isn't configured yet — the owner needs to add a Resend API key (resend.com) to the backend to activate digests.
        </p>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          <button onClick={sendNow} disabled={sending} data-testid="digest-send-now"
            className="flex items-center gap-2 border border-edge px-4 py-2 text-xs text-muted hover:border-gold hover:text-gold disabled:opacity-50">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send my digest now
          </button>
          <button onClick={togglePreview} disabled={loadingPreview} data-testid="digest-preview-btn"
            className="flex items-center gap-2 border border-edge px-4 py-2 text-xs text-muted hover:border-gold hover:text-gold disabled:opacity-50">
            {loadingPreview ? <Loader2 className="h-4 w-4 animate-spin" /> : showPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {showPreview ? "Hide preview" : "Preview Monday's digest"}
          </button>
        </div>
      )}

      {showPreview && preview && (
        <div className="mt-4 space-y-4 border-t border-edge pt-4" data-testid="digest-preview">
          <PreviewSection label="Completions This Week">
            {preview.completions.length === 0 ? (
              <p className="text-xs text-muted">No completions this week.</p>
            ) : preview.completions.map((c, i) => (
              <p key={i} className="text-sm text-[#cfc9bd]"><Check className="mr-1 inline h-3.5 w-3.5 text-gold" /><b className="text-bone">{c.learner_name}</b> completed {c.title}</p>
            ))}
          </PreviewSection>
          <PreviewSection label="Grades Received">
            {preview.grades.length === 0 ? (
              <p className="text-xs text-muted">No new grades this week.</p>
            ) : preview.grades.map((g, i) => (
              <p key={i} className="text-sm text-[#cfc9bd]"><b className="text-bone">{g.title}</b>{g.score != null && <span className="ml-2 border border-gold/50 bg-gold/10 px-1.5 py-0.5 font-mono text-xs text-gold">{g.score}%</span>}</p>
            ))}
          </PreviewSection>
          <PreviewSection label="Still Assigned">
            <p className="text-sm text-[#cfc9bd]">{preview.pending_count} assignment(s) still open.</p>
          </PreviewSection>
          <PreviewSection label="New Heritage Stories">
            {preview.stories.length === 0 ? (
              <p className="text-xs text-muted">No new stories this week.</p>
            ) : preview.stories.map((s, i) => (
              <p key={i} className="text-sm text-[#cfc9bd]"><b className="text-bone">{s.title}</b> <span className="text-xs text-muted">— {s.author_name} · {s.region}</span></p>
            ))}
          </PreviewSection>
          {preview.story_of_month && (
            <PreviewSection label="Story of the Month">
              <p className="text-sm text-[#cfc9bd]"><b className="text-bone">{preview.story_of_month.title}</b> <span className="text-xs text-muted">— {preview.story_of_month.author_name} · ♥ {preview.story_of_month.like_count}</span></p>
            </PreviewSection>
          )}
          <p className="text-xs italic text-muted">Plus: personalized lesson recommendations from the AI tutor, generated fresh for each send.</p>
        </div>
      )}
    </div>
  );
}

function PreviewSection({ label, children }) {
  return (
    <div>
      <p className="mb-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-gold">{label}</p>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function TutorSuggestions() {
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const r = await api.post("/tutor/suggest");
      setSuggestions(r.data.suggestions);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not get suggestions");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mb-8 border border-edge bg-ash p-5" data-testid="tutor-suggestions-card">
      <div className="flex items-center gap-2">
        <Lightbulb className="h-5 w-5 text-gold" />
        <div>
          <p className="text-bone">Tutor Suggestions</p>
          <p className="text-xs text-muted">The AI tutor recommends lessons based on your weakest graded topics</p>
        </div>
      </div>
      {suggestions == null ? (
        <button onClick={fetchSuggestions} disabled={loading} data-testid="get-suggestions-btn"
          className="mt-3 flex items-center gap-2 border border-edge px-4 py-2 text-xs text-muted hover:border-gold hover:text-gold disabled:opacity-50">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lightbulb className="h-4 w-4" />}
          {loading ? "Analyzing your grades…" : "Suggest next lessons"}
        </button>
      ) : (
        <div className="mt-4 space-y-3" data-testid="suggestions-list">
          {suggestions.map((s) => (
            <Link key={s.slug} to={`/lessons/${s.slug}`} data-testid={`suggestion-${s.slug}`}
              className="group block border border-edge bg-ink p-4 transition-colors hover:border-gold/50">
              <p className="flex items-center gap-1 text-bone group-hover:text-gold">
                {s.title} <ArrowRight className="h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-100" />
              </p>
              <p className="mt-1 text-xs leading-relaxed text-muted">{s.reason}</p>
            </Link>
          ))}
          <button onClick={fetchSuggestions} disabled={loading} data-testid="refresh-suggestions-btn"
            className="flex items-center gap-2 text-xs text-muted hover:text-gold disabled:opacity-50">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Lightbulb className="h-3.5 w-3.5" />} Refresh suggestions
          </button>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-ink p-8">
      <Icon className="h-6 w-6 text-gold" />
      <div className="mt-4 font-serif text-4xl text-bone">{value}</div>
      <div className="mt-1 font-mono text-[11px] uppercase tracking-[0.15em] text-muted">{label}</div>
    </div>
  );
}
